import { toast } from "react-toastify";
import { client } from "@/client";
import { AddCarMutation, AddCarMutationVariables, AddCarDocument, FetchTenderByIdDocument, FetchCarsOnTenderDocument, FetchServicesOnTenderDocument } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CustomToastComponent } from "@/components/shared";
import { useMutation } from "@apollo/client/react";

/**
 * Custom hook for adding a car to a tender and handling the necessary cache updates.
 * @param {Object} options - Optional configuration options
 * @param {boolean} [options.disableToast=false] - Whether to disable the success toast
 * @param {boolean} [options.disableRefetch=false] - Whether to disable refetching queries
 * @returns {Object} - Contains the addCarMutation function, loading state, and error state.
 */
export default function useAddCarMutationHook(options: { disableToast?: boolean; disableRefetch?: boolean } = {}): { addCarMutation: any, loading: any, error: any} {
  const { disableToast = false, disableRefetch = false } = options;

  // Hooks
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [addCarMutation, { loading, error }] = useMutation<AddCarMutation, AddCarMutationVariables>(
    AddCarDocument, {
      onCompleted: () => {
        if (!disableToast) {
          toast(CustomToastComponent, {
            toastId: "car-added-success",
            data: {
              title: t("toasts.caraddedsuccessfully"),
              type: "success"
            }
          });
        }

        // Refetch only active FetchServicesOnTender queries for this tender
        // This ensures we make only ONE network call instead of multiple
        // for different parameter combinations
        if (!disableRefetch) {
          client.refetchQueries({
            include: [FetchServicesOnTenderDocument],
            onQueryUpdated(observableQuery) {
              // Only refetch queries for this specific tender
              const variables = observableQuery.options.variables as { tenderId?: number } | undefined;
              return variables?.tenderId === Number(tenderID);
            },
          });
        }
      },
      refetchQueries: disableRefetch ? [] : [
        {
          query: FetchTenderByIdDocument,
          variables: { id: Number(tenderID) }
        },
        {
          query: FetchCarsOnTenderDocument,
          variables: { tenderId: Number(tenderID) }
        },
      ]
    });

  return {
    addCarMutation,
    loading,
    error
  };
}
